import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import useDemoInView from "./useDemoInView";
import {
  X,
  FileText,
  ZoomIn,
  Download,
  MessageCircle,
  Sparkles,
  Wand2,
  Info,
  Camera,
  Upload,
  Search,
  Trash2,
  ArrowUpRight,
  BookMarked,
  Library as LibraryIcon,
} from "lucide-react";

// ---- Brand colors ----
const C = {
  blue: "#2f6df6",
  teal: "#0b8275",
  ink: "#1e293b",
  slate: "#334155",
  body: "#475569",
  muted: "#5C6B80",
  hint: "#5C6B80",
  red: "#ef4444",
  field: "#f5f6f8",
  borderSoft: "#eef0f4",
  cardBorder: "#e8eaef",
};

const PAPER_TITLE = "The study of boundaries in the social sciences";
const PDF_NAME = "1The_study_of_boundaries_in_the_social_sc…";
const QUESTIONS = [
  "Explain Abstract of this paper in 2 lines",
  "Results of the paper",
  "Methods used in this paper",
  "Summarise introduction of this paper",
  "What are the contributions of this paper",
  "Explain the practical implications of this paper",
];
const PICK = 3; // "Summarise introduction of this paper"
const ANSWER =
  "The introduction frames 'boundaries' as a concept that has become central across anthropology, history, political science, social psychology, and sociology. Lamont and Molnár argue that boundaries — both symbolic (the conceptual distinctions we draw) and social (institutionalized differences in resources and opportunities) — offer a unifying lens for studying identity, inequality, and community. The paper sets out to survey these literatures, highlight the relational processes they share, and suggest how future work might connect symbolic and social boundaries more explicitly.";

const words = (s) => s.match(/\S+\s*/g) || [s];

function Dots() {
  return (
    <span style={{ display: "inline-flex", gap: 3 }}>
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          animate={{ opacity: [0.2, 1, 0.2] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
          style={{ width: 5, height: 5, borderRadius: "50%", background: C.blue }}
        />
      ))}
    </span>
  );
}

export default function PdfAnalysisDemo() {
  const rootRef = useRef(null);
  const inView = useDemoInView(rootRef, { once: true, margin: "-80px" });

  const [phase, setPhase] = useState("library"); // 'library' | 'chat'
  const [chatView, setChatView] = useState("suggestions"); // 'suggestions' | 'answer'
  const [answer, setAnswer] = useState("");
  const [thinking, setThinking] = useState(false);
  const [pickedHot, setPickedHot] = useState(false);

  const [cursor, setCursor] = useState({ x: 0, y: 0, click: false });
  const paperRef = useRef(null);
  const questionRefs = useRef([]);

  useEffect(() => {
    if (!inView) return;
    let alive = true;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const moveToEl = (el) => {
      const frame = rootRef.current;
      if (!frame || !el) return;
      const fr = frame.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      setCursor((c) => ({ ...c, x: er.left - fr.left + er.width / 2, y: er.top - fr.top + er.height / 2, click: false }));
    };
    const clickPulse = async () => {
      setCursor((c) => ({ ...c, click: true }));
      await sleep(280);
      setCursor((c) => ({ ...c, click: false }));
    };

    const run = async () => {
      while (alive) {
        // reset
        setPhase("library");
        setChatView("suggestions");
        setAnswer("");
        setThinking(false);
        setPickedHot(false);
        setCursor({ x: 520, y: 360, click: false });
        await sleep(900);
        if (!alive) return;

        // 1) LIBRARY — click the PDF title
        moveToEl(paperRef.current);
        await sleep(800);
        await clickPulse();
        await sleep(220);
        setPhase("chat");
        setChatView("suggestions");
        await sleep(900);
        if (!alive) return;

        // 2) CHAT — click a suggested question
        moveToEl(questionRefs.current[PICK]);
        await sleep(800);
        setPickedHot(true);
        await clickPulse();
        await sleep(200);
        setChatView("answer");
        setThinking(true);
        await sleep(1100);
        if (!alive) return;

        // stream answer
        setThinking(false);
        {
          const w = words(ANSWER);
          let acc = "";
          for (let i = 0; i < w.length; i++) {
            if (!alive) return;
            acc += w[i];
            setAnswer(acc);
            await sleep(22);
          }
        }
        await sleep(3400);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [inView]);

  const libVisible = phase === "library";
  const chatVisible = phase === "chat";

  return (
    <div ref={rootRef} style={styles.frame}>
      {/* ---------------- LIBRARY ---------------- */}
      <div style={{ ...styles.layer, opacity: libVisible ? 1 : 0, pointerEvents: libVisible ? "auto" : "none" }}>
        <div style={styles.libTabs}>
          <div style={styles.libTabActive}>
            My Uploads <Info size={11} color={C.muted} />
          </div>
          <div style={styles.libTab}>
            Saved Abstract <Info size={11} color={C.muted} />
          </div>
        </div>

        <div style={styles.libToolbar}>
          <span style={{ fontSize: 10, color: C.body }}>Formats : PDF, DOC, DOCX, XLSX, CSV</span>
          <span style={styles.tbBtn}><BookMarked size={11} color={C.blue} />Add To Paper Insights</span>
          <span style={styles.tbBtn}><Upload size={11} color={C.teal} />Upload</span>
          <span style={styles.tbBtn}><LibraryIcon size={11} color={C.red} />Import from Zotero</span>
          <span style={styles.tbBtn}><LibraryIcon size={11} color={C.red} />Import from Mendeley</span>
          <span style={styles.searchFiles}>
            <Search size={11} color={C.muted} />
            <span style={{ fontSize: 9.5, color: C.muted }}>Search files …</span>
          </span>
        </div>

        <div style={styles.table}>
          <div style={styles.thead}>
            <span style={{ width: 26 }}><span style={styles.checkbox} /></span>
            <span style={{ width: 22 }}>#</span>
            <span style={{ flex: 2 }}>TITLE</span>
            <span style={{ flex: 1 }}>YEAR</span>
            <span style={{ flex: 1 }}>AUTHORS</span>
            <span style={{ width: 54 }}>Source</span>
            <span style={{ width: 66 }}>ADDED</span>
            <span style={{ width: 62 }}>REMOVE</span>
            <span style={{ width: 78 }}>Papers Insights</span>
          </div>
          <div style={styles.trow}>
            <span style={{ width: 26 }}><span style={styles.checkbox} /></span>
            <span style={{ width: 22, fontSize: 10, color: C.body }}>1</span>
            <span style={{ flex: 2, display: "flex", alignItems: "center", gap: 7 }}>
              <span style={styles.pdfIcon}>PDF</span>
              <span ref={paperRef} style={{ fontSize: 11, color: C.blue }}>{PAPER_TITLE}</span>
            </span>
            <span style={{ flex: 1, fontSize: 10, color: C.body }}>2002</span>
            <span style={{ flex: 1, fontSize: 10, color: C.body }}>M Lamont, V Mol…</span>
            <span style={{ width: 54 }}><ArrowUpRight size={13} color={C.blue} /></span>
            <span style={{ width: 66, fontSize: 9.5, color: C.body }}>29-07-20…</span>
            <span style={{ width: 62 }}><span style={styles.removeBtn}><Trash2 size={11} color="#fff" /></span></span>
            <span style={{ width: 78 }}><span style={styles.notAdded}>Not Added</span></span>
          </div>
          <div style={styles.tfoot}>
            <span style={{ fontSize: 9, color: C.muted }}>Rows per page: 10 ▾</span>
            <span style={{ fontSize: 9, color: C.muted }}>1-1 of 1</span>
          </div>
        </div>
      </div>

      {/* ---------------- CHAT MODAL ---------------- */}
      <motion.div
        animate={{ opacity: chatVisible ? 1 : 0, scale: chatVisible ? 1 : 0.98 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        style={{ ...styles.layer, display: "flex", flexDirection: "column", pointerEvents: chatVisible ? "auto" : "none", background: "#fff" }}
      >
        <div style={styles.modalHead}>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.slate }}>PDF FILE</span>
          <X size={14} color={C.muted} style={{ marginLeft: "auto" }} />
        </div>

        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* PDF viewer */}
          <div style={styles.pdfPane}>
            <div style={styles.pdfBar}>
              <FileText size={12} color={C.red} />
              <span style={{ fontSize: 9.5, color: C.slate, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{PDF_NAME}</span>
              <span style={{ marginLeft: "auto", fontSize: 9, color: C.muted }}>1 / 31</span>
              <span style={{ fontSize: 9, color: C.muted }}>100%</span>
              <ZoomIn size={12} color={C.muted} />
              <Download size={12} color={C.muted} />
            </div>
            <div style={styles.pdfBody}>
              <div style={styles.pdfPage}>
                <div style={{ textAlign: "right", fontSize: 7.5, color: C.body, marginBottom: 10 }}>
                  Annu. Rev. Sociol. 2002. 28:167–95<br />
                  doi: 10.1146/annurev.soc.28.110601.141107<br />
                  Copyright © 2002 by Annual Reviews. All rights reserved
                </div>
                <div style={{ textAlign: "center", fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: C.ink, margin: "14px 0" }}>
                  THE STUDY OF BOUNDARIES IN THE SOCIAL SCIENCES
                </div>
                <div style={{ fontSize: 9, fontWeight: 600, color: C.ink }}>Michèle Lamont and Virág Molnár</div>
                <div style={{ fontSize: 8, fontStyle: "italic", color: C.body, marginBottom: 10 }}>
                  Department of Sociology, Princeton University, Princeton, New Jersey 08540;
                  e-mail: Mlamont@princeton.edu, vmolnar@phoenix.princeton.edu
                </div>
                <div style={{ fontSize: 8.5, color: C.slate, marginBottom: 8 }}>
                  <b>Key Words</b>&nbsp;&nbsp;culture, identity, inequality, community, borders
                </div>
                <div style={{ fontSize: 8.5, lineHeight: 1.6, color: C.slate }}>
                  <b>■ Abstract</b>&nbsp;&nbsp;In recent years, the concept of boundaries has been at the center of
                  influential research agendas in anthropology, history, political science, social psychology, and
                  sociology. This article surveys some of these developments while describing the value added provided
                  by the concept, particularly concerning the study of relational processes. It discusses literatures
                  on (a) social and collective identity; (b) class, ethnic/racial, and gender/sex inequality; (c)
                  professions, knowledge, and science; and (d) communities, national identities, and spatial
                  boundaries.
                </div>
              </div>
            </div>
          </div>

          {/* Ask ResearchPal */}
          <div style={styles.askPane}>
            <div style={styles.askHead}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.blue }}>Ask ResearchPal</div>
              <div style={{ fontSize: 9, color: C.muted }}>Your AI chat for smarter insights</div>
              <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
                <span style={styles.askTabActive}>General (10)</span>
                <span style={{ fontSize: 10, color: C.muted }}>My Conversations</span>
              </div>
            </div>

            <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
              {/* suggestions */}
              <div style={{ ...styles.askScroll, opacity: chatView === "suggestions" ? 1 : 0, pointerEvents: chatView === "suggestions" ? "auto" : "none" }}>
                {QUESTIONS.map((q, i) => (
                  <div
                    key={i}
                    ref={(el) => (questionRefs.current[i] = el)}
                    style={{ ...styles.qItem, background: pickedHot && i === PICK ? "#eaf1ff" : "transparent" }}
                  >
                    <MessageCircle size={12} color={C.muted} />
                    Q : {q}
                  </div>
                ))}
              </div>

              {/* answer */}
              <div style={{ ...styles.askChat, opacity: chatView === "answer" ? 1 : 0, pointerEvents: chatView === "answer" ? "auto" : "none" }}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                  <span style={styles.userBubble}>{QUESTIONS[PICK]}</span>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <span style={styles.aiAvatar}>
                    <Sparkles size={11} color={C.blue} />
                  </span>
                  <span style={styles.aiBubble}>{thinking ? <Dots /> : answer}</span>
                </div>
              </div>
            </div>

            <div style={styles.askFooter}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <span style={styles.footBtn}>
                  <Sparkles size={10} /> ANALYZE
                </span>
                <span style={styles.footBtn}>
                  <Wand2 size={10} /> REMIX
                </span>
              </div>
              <div style={styles.askInput}>
                <span style={{ fontSize: 10, color: C.muted }}>Ask or share your thoughts...</span>
                <Info size={12} color={C.hint} style={{ marginLeft: "auto" }} />
                <Camera size={12} color={C.hint} />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles — no sidebar, fills full container width
// ---------------------------------------------------------------------------
const styles = {
  frame: {
    position: "relative",
    width: "100%",
    height: 480,
    borderRadius: 14,
    overflow: "hidden",
    background: "#fff",
    border: `1px solid ${C.cardBorder}`,
    boxShadow: "0 14px 44px rgba(30,41,59,0.14)",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  layer: { position: "absolute", inset: 0, transition: "opacity 0.35s ease" },

  // library
  libTabs: { display: "flex", borderBottom: `1px solid ${C.borderSoft}` },
  libTab: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: 13, fontSize: 11, color: C.muted },
  libTabActive: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: 13, fontSize: 11, color: C.ink, fontWeight: 600, borderBottom: `2px solid ${C.ink}` },
  libToolbar: { display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", flexWrap: "wrap" },
  tbBtn: { display: "flex", alignItems: "center", gap: 5, fontSize: 9.5, color: C.slate, background: "#f1f3f7", borderRadius: 7, padding: "6px 9px" },
  searchFiles: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 5, background: C.field, border: `1px solid ${C.borderSoft}`, borderRadius: 7, padding: "6px 10px" },

  table: { margin: "4px 14px", border: `1px solid ${C.borderSoft}`, borderRadius: 10, overflow: "hidden" },
  thead: { display: "flex", alignItems: "center", gap: 6, background: C.teal, color: "#fff", fontSize: 9, fontWeight: 600, padding: "10px 12px" },
  trow: { display: "flex", alignItems: "center", gap: 6, padding: "12px", borderBottom: `1px solid ${C.borderSoft}` },
  tfoot: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 16, padding: "8px 12px" },
  checkbox: { display: "inline-block", width: 12, height: 12, borderRadius: 3, border: `1.5px solid rgba(255,255,255,0.7)` },
  pdfIcon: { fontSize: 6.5, fontWeight: 700, color: "#fff", background: C.red, borderRadius: 3, padding: "3px 3px" },
  removeBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 20, background: C.red, borderRadius: 5 },
  notAdded: { fontSize: 8.5, color: C.body, background: "#eceef2", borderRadius: 6, padding: "4px 8px" },

  // chat modal
  modalHead: { display: "flex", alignItems: "center", padding: "9px 14px", borderBottom: `1px solid ${C.borderSoft}` },
  pdfPane: { width: "52%", display: "flex", flexDirection: "column", borderRight: `1px solid ${C.borderSoft}`, background: "#f4f5f7" },
  pdfBar: { display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderBottom: `1px solid ${C.borderSoft}`, background: "#fff" },
  pdfBody: { flex: 1, overflow: "hidden", padding: 12, display: "flex", justifyContent: "center" },
  pdfPage: { width: "100%", background: "#fff", border: `1px solid ${C.borderSoft}`, borderRadius: 4, boxShadow: "0 2px 8px rgba(0,0,0,0.05)", padding: "16px 18px", overflow: "hidden" },

  askPane: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0 },
  askHead: { padding: "12px 14px 8px", borderBottom: `1px solid ${C.borderSoft}` },
  askTabActive: { fontSize: 10, color: C.blue, fontWeight: 600, borderBottom: `2px solid ${C.blue}`, paddingBottom: 4 },
  askScroll: { position: "absolute", inset: 0, padding: "8px 12px", display: "flex", flexDirection: "column", transition: "opacity 0.3s ease" },
  qItem: { display: "flex", alignItems: "center", gap: 7, padding: "9px 8px", borderBottom: "1px solid #f3f4f7", fontSize: 10, color: C.slate, borderRadius: 6 },
  askChat: { position: "absolute", inset: 0, padding: "10px 12px", transition: "opacity 0.3s ease", overflow: "hidden" },
  userBubble: { fontSize: 10, color: "#fff", background: C.blue, borderRadius: "10px 10px 2px 10px", padding: "8px 11px", maxWidth: "80%" },
  aiAvatar: { flexShrink: 0, width: 20, height: 20, borderRadius: "50%", background: "#eaf1ff", display: "flex", alignItems: "center", justifyContent: "center" },
  aiBubble: { fontSize: 10, color: C.slate, lineHeight: 1.6, background: C.field, borderRadius: "2px 10px 10px 10px", padding: "8px 11px", maxWidth: "82%" },
  askFooter: { padding: "8px 12px", borderTop: `1px solid ${C.borderSoft}` },
  footBtn: { flex: 1, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, fontSize: 10, color: "#64748b", background: "#eef0f4", borderRadius: 7, padding: 7 },
  askInput: { display: "flex", alignItems: "center", gap: 6, background: C.field, border: `1px solid ${C.borderSoft}`, borderRadius: 8, padding: "8px 10px" },

  cursor: { position: "absolute", top: 0, left: 0, width: 20, height: 20, zIndex: 60, pointerEvents: "none", transition: "transform 0.7s cubic-bezier(0.22, 0.61, 0.36, 1)" },
  clickRipple: { position: "absolute", top: 2, left: 2, width: 16, height: 16, borderRadius: "50%", background: "rgba(47,109,246,0.4)" },
};
